import { useEffect, useState } from 'react'
import { getFormInstanceApi } from '../api'
import { getImageDimensions, getPdfDocument } from '../utility'

const PAGE_SPACING = 30

const calculateGlobalPdfScale = async (metadataList, containerWidth) => {
  let scale = 1
  for (let metadata of metadataList) {
    const type = metadata.type
    if (type === 'form' || type === 'images') {
      for (let dim of metadata.dimensions) {
        const { width, height } = dim
        scale = Math.min(scale, containerWidth / width)
      }
    }
    if (type === 'pdf') {
      for (let page of metadata.pagesProxy) {
        const viewport = page.getViewport({ scale: 1 })
        scale = Math.min(scale, containerWidth / viewport.width)
      }
    }
  }
  return scale
}

const buildImagesLayouts = ({ id, dimensions, startOffset = 0, scale, spacing = 30 }) => {
  let top = startOffset

  const layouts = dimensions.map(({ width: originalWidth, height: originalHeight, url }, index) => {
    const aspectRatio = originalWidth / originalHeight
    const width = originalWidth * scale
    const height = width / aspectRatio
    const layout = { url, id, index, scale, width, height, top, bottom: top + height }
    top += height + spacing
    return { ...layout, type: 'image' }
  })

  return layouts
}

const buildFormLayouts = ({ id, form, startOffset = 0, containerWidth, scale }) => {
  let top = startOffset
  const spacing = 30 // default of FormV3 component
  const pages = Object.values(form.pages ?? {})
  if (pages.length === 0) return []
  let width = containerWidth * scale
  for (let page of pages) {
    const { width: originalWidth, height: originalHeight } = page.page_sizes[form.page_width]
    const aspectRatio = originalWidth / originalHeight
    const height = width / aspectRatio
    top += height + spacing
  }
  const bottom = top - spacing
  const height = bottom - startOffset
  const layout = { width, height, scale, top: startOffset, bottom }

  return [{ ...layout, type: 'form', id, numPages: pages.length, formId: form.id }]
}

const buildPdfLayouts = ({ id, pagesProxy, startOffset = 0, scale, spacing = 30 }) => {
  let top = startOffset

  const layouts = pagesProxy.map((page, index) => {
    const viewport = page.getViewport({ scale })
    const width = viewport.width
    const height = viewport.height
    const layout = { id, index, pageProxy: page, scale, width, height, top, bottom: top + height }
    top += height + spacing

    return { ...layout, type: 'pdf' }
  })

  return layouts
}

const generateLayouts = (metadataList, globalScale, containerWidth) => {
  let startOffset = 0
  const scale = globalScale
  const spacing = PAGE_SPACING * globalScale
  const pageLayouts = []

  const newMetadataList = metadataList.map((doc) => {
    let layouts = []
    switch (doc.type) {
      case 'pdf':
        layouts = buildPdfLayouts({ id: doc.id, pagesProxy: doc.pagesProxy, startOffset, scale, spacing })
        break
      case 'form':
        layouts = buildFormLayouts({ id: doc.id, form: doc.form, startOffset, containerWidth, scale })
        break
      case 'images':
        layouts = buildImagesLayouts({ id: doc.id, dimensions: doc.dimensions, startOffset, scale, spacing })
        break
    }
    pageLayouts.push(...layouts)
    startOffset = pageLayouts.at(-1).bottom + spacing
    return { ...doc, layouts }
  })

  return { metadataList: newMetadataList, pageLayouts }
}

const fetchPdfMetadata = async ({ url }) => {
  const documentProxy = await getPdfDocument({ url })
  const pageNumbers = Array.from({ length: documentProxy.numPages }, (_, i) => i + 1)
  const pagesProxy = await Promise.all(pageNumbers.map((num) => documentProxy.getPage(num)))
  return { documentProxy, pagesProxy }
}

const fetchFormMetadata = async ({ formId, width }) => {
  const { data } = await getFormInstanceApi({ id: formId, width })
  const pageFiles = Object.values(data?.form.page_files ?? {})
  const dimensions = await Promise.all(pageFiles.map(getImageDimensions))
  return { form: data?.form, dimensions }
}

const fetchImagesMetadata = async ({ urls }) => {
  const dimensions = await Promise.all(urls.map(getImageDimensions))
  return { dimensions }
}

export const loadDocumentMetadata = async (document, width) => {
  return Promise.all(
    document.map(async (doc) => {
      let metadata = {}
      if (doc.type === 'pdf') metadata = await fetchPdfMetadata({ url: doc.url })
      if (doc.type === 'form') metadata = await fetchFormMetadata({ formId: doc.formId, width })
      if (doc.type === 'images') metadata = await fetchImagesMetadata({ urls: doc.urls })
      return { ...doc, ...metadata }
    })
  )
}

export const useProcessDocument = ({ document, containerRef, setScale }) => {
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [documentMetadataList, setDocumentMetadataList] = useState([])
  const [pageLayouts, setPageLayouts] = useState([])

  useEffect(() => {
    const container = containerRef.current
    if (!document?.length || !container) {
      setIsLoading(false)
      return
    }

    const processDocument = async () => {
      setIsLoading(true)
      setIsError(false)
      try {
        const containerWidth = container.clientWidth
        const metadataList = await loadDocumentMetadata(document, containerWidth)
        const globalScale = await calculateGlobalPdfScale(metadataList, containerWidth)

        const { pageLayouts } = generateLayouts(metadataList, globalScale, containerWidth)

        setDocumentMetadataList(metadataList)
        setPageLayouts(pageLayouts)
      } catch {
        setIsError(true)
      } finally {
        setIsLoading(false)
      }
    }

    processDocument()
  }, [document])

  return { documentMetadataList, pageLayouts, isLoading }
}
